import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Bell, Sparkles } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { Kicker } from "@/components/landing/Section";
import { Floater, fade } from "@/components/landing/HeroLive";
import { bySlug } from "@/lib/trolls";
import { SOCIAL_LINKS } from "@/config/links";

const INSTAGRAM = SOCIAL_LINKS.find((s) => s.key === "instagram").url;

const ComingSoonOval = ({ label, sub, count, testId }) => (
  <div className="absolute inset-0 grid place-items-center pointer-events-none">
    <motion.div
      initial={{ opacity: 0, scale: 0.7, rotate: -16 }}
      animate={{ opacity: 1, scale: 1, rotate: -7 }}
      transition={{ delay: 0.3, type: "spring", stiffness: 140, damping: 13 }}
      data-testid={testId}
      className="relative w-[80%] max-w-[400px] aspect-[2/1] rounded-[50%] bg-parchment/85 backdrop-blur-md border-[3px] border-amber shadow-journal-lg grid place-items-center text-center"
    >
      <span className="absolute -inset-3 rounded-[50%] border-2 border-amber/50 animate-[tfj-halo_2.8s_ease-out_infinite]" />
      <span className="absolute inset-2 rounded-[50%] border border-dashed border-amber/70" />
      <div className="px-8">
        <div className="inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] font-extrabold uppercase tracking-[0.28em] text-amber-dark">
          <Sparkles className="w-3 h-3" /> {sub}
        </div>
        <div className="font-display italic text-3xl sm:text-[2.6rem] leading-tight font-semibold text-ink mt-1">{label}</div>
        <div className="mt-1 text-sm font-extrabold text-moss">{count}</div>
      </div>
    </motion.div>
  </div>
);

const SoonPhoto = ({ slide, tr, s }) => (
  <motion.div {...fade(0.15)} className="relative lg:col-span-6">
    <div className="relative rounded-[20px] p-2.5 bg-paper border border-line shadow-journal-lg rotate-[-1deg]">
      <img
        src={slide.img}
        alt={tr.names[slide.key]}
        data-testid={`hero-slide-${slide.key}-image`}
        className="w-full h-[340px] sm:h-[440px] lg:h-[560px] object-cover rounded-[14px]"
      />
      <div className="absolute inset-2.5 rounded-[14px] bg-gradient-to-t from-ink/35 via-ink/5 to-transparent" />
      <ComingSoonOval label={tr.comingSoon} sub={tr.soonSub} count={s.stats[0].join(" ")} testId={`hero-slide-${slide.key}-soon`} />
    </div>
    <Floater troll={bySlug(slide.troll)} pos="-left-4 sm:-left-10 -bottom-10 h-36 sm:h-52" />
  </motion.div>
);

export default function HeroSoon({ slide, onPlayNow, onPage = false }) {
  const { t } = useLang();
  const tr = t.trails;
  const s = tr.slides[slide.key];
  const wrap = onPage ? "" : "max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 ";
  return (
    <div className={`${wrap}grid lg:grid-cols-12 gap-14 lg:gap-10 items-center`} data-testid={`hero-slide-${slide.key}`}>
      <div className="lg:col-span-6 lg:pr-6">
        <motion.div {...fade(0)} className="flex flex-wrap items-center gap-3">
          <Kicker>{s.kicker}</Kicker>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber/15 text-amber-dark px-3 py-1 text-xs font-extrabold uppercase tracking-wider">
            <span className="w-1.5 h-1.5 rounded-full bg-amber animate-pulse" /> {tr.comingSoon}
          </span>
        </motion.div>
        <motion.h1 {...fade(0.08)} data-testid={`hero-slide-${slide.key}-title`} className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] text-ink mt-5">
          {s.title}
        </motion.h1>
        <motion.p {...fade(0.16)} className="text-base md:text-lg text-ink-muted leading-relaxed mt-6 max-w-xl">{s.pitch}</motion.p>
        <motion.div {...fade(0.24)} className="flex flex-wrap items-center gap-3 mt-9">
          {!onPage && (
            <Link
              to={`/trails/${slide.key}`}
              data-testid={`hero-slide-${slide.key}-read-more`}
              className="rounded-full bg-ink hover:bg-[#1f1c18] text-parchment px-7 py-4 text-base font-extrabold shadow-journal inline-flex items-center gap-2 transition-[background-color,transform] duration-200 hover:-translate-y-0.5"
            >
              {tr.readMore} <ArrowRight className="w-4 h-4" />
            </Link>
          )}
          <a
            href={INSTAGRAM}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`hero-slide-${slide.key}-follow`}
            className="rounded-full bg-moss hover:bg-moss-dark text-parchment px-7 py-4 text-base font-extrabold shadow-journal inline-flex items-center gap-2 transition-[background-color,transform] duration-200 hover:-translate-y-0.5"
          >
            <Bell className="w-4 h-4" /> {tr.follow}
          </a>
          <button
            type="button"
            onClick={onPlayNow}
            data-testid={`hero-slide-${slide.key}-play-now`}
            className="rounded-full border-2 border-ink/80 text-ink px-6 py-3.5 text-base font-extrabold inline-flex items-center gap-2 hover:bg-ink hover:text-parchment transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> {tr.playNow}
          </button>
        </motion.div>
        <motion.p {...fade(0.3)} className="mt-6 text-sm font-bold text-rowan">{s.note}</motion.p>
        <motion.dl {...fade(0.36)} className="grid grid-cols-3 gap-4 mt-10 pt-8 border-t border-dashed border-line max-w-md">
          {s.stats.map(([n, label]) => (
            <div key={label}>
              <dt className="font-display text-3xl font-semibold text-ink tabular-nums">{n}</dt>
              <dd className="text-xs font-bold text-ink-muted mt-1 leading-tight">{label}</dd>
            </div>
          ))}
        </motion.dl>
      </div>
      <SoonPhoto slide={slide} tr={tr} s={s} />
    </div>
  );
}
